import { useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { NsSecondaryCta } from 'components/common/ns-secondary-cta/NsSecondaryCta';
import { NsAccordion } from 'components/common/ns-accordion/NsAccordion';
import { NsExpander } from 'components/common/ns-expander/NsExpander';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import {
  selectTariffTypes,
  selectProducts,
  selectTariffTypeFilter,
  setTariffTypeFilter,
  setIsDualRate,
  selectIsDualRate,
  initialState,
  selectIsLoading
} from 'state/quote/quote-slice';
import { getQuoteThunk } from 'state/quote/quote-thunks';
import { useQuotePayload } from 'components/direct-sales/quote/hooks/useQuotePayload';
import { useEnergyUsage } from 'hooks/useEnergyUsage';
import { selectIsDirectConsumption } from 'state/consumption-entry/consumption-entry-slice';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { setConsumption } from 'state/consumption/consumption-slice';
import { FUEL_TYPES } from 'config/app-config';
import { selectConsumptionType } from 'state/consumption-entry/consumption-entry-slice';
import { useTariffFilters } from '../hooks/useTariffFilters';
import { pushEvent } from 'state/analytics/analytics-slice';
import { useAppDispatch } from 'hooks/useAppDispatch';

export const testId = 'quoteTestId';
export const testIdVoicesalesWindbackAlert = 'testIdVoicesalesWinbackAlert';

const RATE_TYPES = {
  TWO_RATE: 'twoRate',
  SINGLE_RATE: 'singleRate'
};

export const TariffFilters = () => {
  const dispatch = useAppDispatch();
  const getQuotePayload = useQuotePayload();
  const getEnergyUsage = useEnergyUsage();
  const {
    hasAnyFilter,
    showRateTypeFilters,
    showTariffTypeFilters
  } = useTariffFilters();
  const selectedFuelType = useSelector(selectFuelType, shallowEqual);
  const isDualRate = useSelector(selectIsDualRate, shallowEqual);
  const tariffTypes = useSelector(selectTariffTypes, shallowEqual) as string[];
  const products = useSelector(selectProducts, shallowEqual);
  const isLoading = useSelector(selectIsLoading, shallowEqual);
  const tariffTypeFilter = useSelector(selectTariffTypeFilter, shallowEqual);
  const uniqueTariffTypes = useMemo(
    () => [...new Set(tariffTypes.filter((t) => !!t))],
    [tariffTypes]
  );
  const consumptionType = useSelector(selectConsumptionType, shallowEqual);
  const isDirectConsumption = useSelector(
    selectIsDirectConsumption,
    shallowEqual
  );

  const productsByTariffType = uniqueTariffTypes.reduce<
    Record<string, typeof products>
  >((acc, tariffType: string) => {
    return {
      ...acc,
      [tariffType]: products.filter((product) => {
        const til = product.electricityTil || product.gasTil;
        return til?.tariffType === tariffType;
      })
    };
  }, {} as { [key: string]: typeof products });

  const tariffTypeOptions = [
    {
      label: `Show all (${products.length})`,
      value: uniqueTariffTypes.join('-')
    },
    ...Object.entries(productsByTariffType).map(([tariffType, products]) => ({
      label: (
        <>
          {tariffType} ({products.length})
        </>
      ),
      value: tariffType
    }))
  ];

  const rateTypeOptions = [
    {
      label: 'Single rate',
      value: RATE_TYPES.SINGLE_RATE
    },
    {
      label: 'Two-rate',
      value: RATE_TYPES.TWO_RATE
    }
  ];

  const handleChangeRateType = async (event) => {
    const { value } = event.target;

    const isDualRate = value === RATE_TYPES.TWO_RATE;
    const consolidatedUsage = getEnergyUsage(
      consumptionType,
      selectedFuelType,
      isDualRate
    );
    dispatch(setIsDualRate(isDualRate));
    dispatch(
      pushEvent({
        category: 'nepsales-energy',
        action: 'radio-button-click',
        label: `electricitytypes - ${value}`
      })
    );

    if (!isDirectConsumption) {
      dispatch(setConsumption(consolidatedUsage));
    }
    const payload = getQuotePayload({
      isEconomy7: isDualRate,
      ...(!isDirectConsumption && {
        electricityConsumption: consolidatedUsage[FUEL_TYPES.ELECTRIC]
      })
    });

    try {
      await dispatch(getQuoteThunk(payload));
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangeTariffType = (event) => {
    const { value } = event.target;
    dispatch(setTariffTypeFilter(value.split('-')));
    dispatch(
      pushEvent({
        action: 'radio-button-click',
        category: 'nepsales-energy',
        label: `tariff-types - ${value}`
      })
    );
  };

  const handleResetFilters = async () => {
    if (isLoading) {
      return;
    }

    dispatch(setIsDualRate(initialState.isDualRate));
    dispatch(setTariffTypeFilter(uniqueTariffTypes));

    const shouldFetchTariffs = isDualRate !== initialState.isDualRate;

    try {
      const payload = getQuotePayload({ isEconomy7: initialState.isDualRate });
      shouldFetchTariffs && (await dispatch(getQuoteThunk(payload)));
    } catch (error) {
      console.error(error);
    }
  };

  const rateTypeValue =
    isDualRate === true ? RATE_TYPES.TWO_RATE : RATE_TYPES.SINGLE_RATE;
  const tariffTypeValue = tariffTypeFilter && tariffTypeFilter.join('-');

  useEffect(() => {
    dispatch(setTariffTypeFilter(uniqueTariffTypes));
  }, [uniqueTariffTypes, dispatch]);

  if (!hasAnyFilter) {
    return null;
  }

  return (
    <>
      <NsAccordion heading="Tariff filters">
        {showTariffTypeFilters && (
          <NsExpander heading="Tariff type" open>
            <NsRadioInput
              name="tariffTypeFilter"
              value={tariffTypeValue}
              key={tariffTypeValue}
              options={tariffTypeOptions}
              onChange={handleChangeTariffType}
            />
          </NsExpander>
        )}
        {showRateTypeFilters && (
          <NsExpander heading="Electricity rates" open>
            <NsRadioInput
              helper="What's the difference?"
              tipDetail={`
                  You have a choice of a single unit rate or a different price 
                  at night compared to the day. This is useful if you use a lot 
                  of electricity at night, but can be more expensive if you don't.
                `}
              name="rateType"
              value={rateTypeValue}
              key={rateTypeValue}
              options={rateTypeOptions}
              onChange={handleChangeRateType}
              disabled={isLoading}
            />
          </NsExpander>
        )}
      </NsAccordion>
      <NsSecondaryCta icon="cross" type="text" onClick={handleResetFilters}>
        Reset filters
      </NsSecondaryCta>
    </>
  );
};
